/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useEffect, useState } from "react"
import { Row, Col, Button, List, Checkbox, Radio } from "@duxton-web/ui"
import { useSelector } from "react-redux"
import { minorUnitToCurrency } from "utils/currency"
import { AppState } from "store/store.type"
import { Item, Modifier, ModifierGroup } from "types/models/menu"
import { numberWithCommas, renderModifierGroupRule } from "utils/misc"
import { PreviewDrawer } from "components/drawers/PreviewDrawer"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { modifierGroupPreviewDrawer } from "./ModifierGroups.styles"

interface props {
  showPreviewModifierGroupDrawer: boolean
  previewModifierGroup: ModifierGroup
  previewItem: Item
  onToggleDrawer: any
}

const radioStyle = {
  height: "30px",
  lineHeight: "30px",
}

const ModifierGroupPreviewDrawer: FC<props> = ({
  showPreviewModifierGroupDrawer,
  previewModifierGroup,
  previewItem,
  onToggleDrawer,
}: props) => {
  const [itemPrice, setItemPrice] = useState<number>(0)

  const [checkboxSelected, setCheckboxSelected] = useState<number>(0)

  const [isBtnEnabled, setBtnEnabled] = useState(false)

  const { outlet, outletV2, enableTroyNewMerchantEndpoint } = useSelector(
    (state: AppState) => state.menu,
  )

  const currencyCode = enableTroyNewMerchantEndpoint
    ? outletV2?.currency?.code
    : outlet?.currency

  const textMap = useTranslationsMap({
    pickX: "menu.modifierGroup.preview.pickX",
    pickXtoY: "menu.modifierGroup.preview.pickXtoY",
    pickUpToXOptional: "menu.modifierGroup.preview.pickUpToXoptional",
  })

  const handleRadioBtn = (modPrice: number) => {
    const origPrice = minorUnitToCurrency(previewItem.priceInMin, currencyCode)
    setItemPrice(origPrice + modPrice)
    setBtnEnabled(true)
  }

  const handleCheck = (value: boolean, modPrice: number) => {
    let newCheckboxSelected = checkboxSelected
    if (value) {
      newCheckboxSelected += 1
      setCheckboxSelected(newCheckboxSelected)
      setBtnEnabled(
        newCheckboxSelected >= previewModifierGroup.selectionRangeMin,
      )
      setItemPrice(itemPrice + modPrice)
    } else {
      newCheckboxSelected -= 1
      setCheckboxSelected(newCheckboxSelected)
      setBtnEnabled(
        newCheckboxSelected - 1 >= previewModifierGroup.selectionRangeMin,
      )
      setItemPrice(itemPrice - modPrice)
    }
  }

  const modifierGroupRule = () => {
    return renderModifierGroupRule(previewModifierGroup, textMap)
  }

  const isSingleSelection = modifierGroupRule().requireExactly

  const greenButtonClass = "item-button-enabled"
  const greyButtonClass = "item-button"

  const buttonClass = isBtnEnabled ? greenButtonClass : greyButtonClass

  const isMaxMultipleSelection =
    (!isSingleSelection &&
      previewModifierGroup.selectionRangeMax ===
        previewModifierGroup.selectionRangeMin) ||
    previewModifierGroup.selectionRangeMin === 0

  useEffect(() => {
    const origPrice = minorUnitToCurrency(previewItem.priceInMin, currencyCode)
    if (isMaxMultipleSelection) {
      setCheckboxSelected(previewModifierGroup.selectionRangeMin)
      setBtnEnabled(true)
      const sumModPrice = previewModifierGroup.modifiers.reduce((tot, arr) => {
        return tot + arr.priceInMin
      }, 0)
      setItemPrice(origPrice + sumModPrice)
    } else {
      setItemPrice(origPrice)
    }
  }, [
    previewItem.priceInMin,
    isSingleSelection,
    previewModifierGroup.selectionRangeMax,
    previewModifierGroup.selectionRangeMin,
    previewModifierGroup.modifiers,
    isMaxMultipleSelection,
    currencyCode,
  ])

  return (
    <PreviewDrawer
      title={useTranslation("menu.modifierGroup.heading.preview")}
      visible={showPreviewModifierGroupDrawer}
      onClose={() => onToggleDrawer(false)}
    >
      <div css={modifierGroupPreviewDrawer}>
        <div className="preview-content">
          {previewItem.imageURL && (
            <Row className="item-img-wrapper">
              <Col span={24}>
                <img className="item-img" src={previewItem.imageURL} />
              </Col>
            </Row>
          )}
          <Row className="item-overview">
            <Col span={12} className="item-name">
              {previewItem.itemName}
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <p className="item-price">
                {Number(previewItem.priceInMin) / 100}
              </p>
              <p className="base-price-label">
                {useTranslation("menu.preview.basePrice")}
              </p>
            </Col>
          </Row>
          <Row className="item-description">
            <Col span={24}>
              <p>{previewItem.description}</p>
            </Col>
          </Row>
          <div className="item-modifier">
            <Row>
              <Col span={24}>
                <p className="modifier-group-name">
                  {previewModifierGroup.modifierGroupName} &nbsp;&nbsp;
                  <span>{modifierGroupRule().modifierGroupRule}</span>
                </p>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                {isSingleSelection ? (
                  <Radio.Group
                    onChange={(e: any) => {
                      handleRadioBtn(e.target.value)
                    }}
                  >
                    {previewModifierGroup.modifiers?.map(
                      (modifier: Modifier) => (
                        <Radio
                          style={radioStyle}
                          value={modifier.priceInMin}
                          key={modifier.modifierID}
                        >
                          <div className="modifier-wrapper">
                            <span>{modifier.modifierName}</span>
                            <span>
                              {modifier.priceInMin !== 0
                                ? `+${numberWithCommas(
                                    Number(modifier.priceInMin),
                                  )}`
                                : 0}
                            </span>
                          </div>
                        </Radio>
                      ),
                    )}
                  </Radio.Group>
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={previewModifierGroup.modifiers}
                    renderItem={modifier => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            <Checkbox
                              defaultChecked={
                                previewModifierGroup.selectionRangeMin ===
                                previewModifierGroup.selectionRangeMax
                              }
                              onChange={(e: any) => {
                                handleCheck(
                                  e.target.checked,
                                  modifier.priceInMin,
                                )
                              }}
                              onClick={(e: any) => {
                                e.stopPropagation()
                              }}
                            />
                          }
                          description={
                            <div className="modifier-wrapper">
                              <span>{modifier.modifierName}</span>
                              <span>
                                {modifier.priceInMin !== 0
                                  ? `+${numberWithCommas(
                                      Number(modifier.priceInMin),
                                    )}`
                                  : 0}
                              </span>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Col>
            </Row>
          </div>
          <Row className={buttonClass}>
            <Col span={24}>
              <Button type="primary" block>
                {useTranslation("menu.buttons.addToBasket")} -{" "}
                {numberWithCommas(itemPrice)}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </PreviewDrawer>
  )
}

export default ModifierGroupPreviewDrawer
